import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  PLATFORMS,
  Platform,
  CreateMaintenanceInput,
  MaintenanceWindow,
  UpdateMaintenanceInput,
} from './maintenance-types';
import { Game } from '@/features/games/games-types';

const schema = z
  .object({
    game_id: z.string().uuid('Game is required'),
    platform: z.enum(['ios', 'android', 'web', 'harmonyos']),
    starts_at: z.string().min(1, 'Start time is required'),
    ends_at: z.string().min(1, 'End time is required'),
    message: z.string().min(1, 'Message is required').max(1024),
    is_active: z.boolean().default(true),
  })
  .refine((d) => new Date(d.starts_at) < new Date(d.ends_at), {
    message: 'End time must be after start time',
    path: ['ends_at'],
  });
type FormData = z.infer<typeof schema>;

interface MaintenanceFormProps {
  initial?: MaintenanceWindow;
  games: Game[];
  onSubmit: (data: CreateMaintenanceInput | UpdateMaintenanceInput) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
}

function toLocalISO(iso: string): string {
  return iso.slice(0, 16);
}

export function MaintenanceForm({
  initial,
  games,
  onSubmit,
  onCancel,
  isSubmitting,
}: MaintenanceFormProps): JSX.Element {
  const isEdit = !!initial;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema) as unknown as Resolver<FormData>,
    defaultValues: initial
      ? {
          game_id: initial.game_id,
          platform: initial.platform,
          starts_at: toLocalISO(initial.starts_at),
          ends_at: toLocalISO(initial.ends_at),
          message: initial.message,
          is_active: initial.is_active,
        }
      : { game_id: '', platform: 'android', is_active: true },
  });

  useEffect(() => {
    if (initial) {
      reset({
        game_id: initial.game_id,
        platform: initial.platform,
        starts_at: toLocalISO(initial.starts_at),
        ends_at: toLocalISO(initial.ends_at),
        message: initial.message,
        is_active: initial.is_active,
      });
    }
  }, [initial, reset]);

  const isActive = watch('is_active');
  const selectedGameId = watch('game_id');
  const selectedPlatform = watch('platform');

  function handleFormSubmit(data: FormData): void {
    if (isEdit) {
      // Red Team F#1: strip `platform` (and `game_id`) from the PATCH payload.
      // The global API ValidationPipe has forbidNonWhitelisted=true, so
      // including `platform` returns 400.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { platform: _stripped, game_id: _gid, ...patchPayload } = data;
      void onSubmit(patchPayload as UpdateMaintenanceInput);
    } else {
      void onSubmit({
        game_id: data.game_id ?? '',
        platform: data.platform,
        starts_at: new Date(data.starts_at).toISOString(),
        ends_at: new Date(data.ends_at).toISOString(),
        message: data.message,
        is_active: data.is_active,
      } as CreateMaintenanceInput);
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {!isEdit && (
        <div className="space-y-2">
          <Label htmlFor="game_id">Game</Label>
          <Select value={selectedGameId ?? ''} onValueChange={(v) => setValue('game_id', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a game" />
            </SelectTrigger>
            <SelectContent>
              {games.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.package_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.game_id && (
            <p className="text-sm text-destructive">Game is required</p>
          )}
        </div>
      )}
      <div className="space-y-2">
        {/* Red Team F#1: platform is immutable on PATCH. */}
        <Label htmlFor="platform">Platform</Label>
        <Select
          value={selectedPlatform ?? 'android'}
          onValueChange={(v) => setValue('platform', v as Platform)}
          disabled={isEdit}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a platform" />
          </SelectTrigger>
          <SelectContent>
            {PLATFORMS.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isEdit && (
          <p className="text-xs text-muted-foreground">
            Platform is locked after creation. To change, delete and recreate.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="starts_at">Start</Label>
          <Input id="starts_at" type="datetime-local" {...register('starts_at')} />
          {errors.starts_at && (
            <p className="text-sm text-destructive">{errors.starts_at.message as string}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="ends_at">End</Label>
          <Input id="ends_at" type="datetime-local" {...register('ends_at')} />
          {errors.ends_at && (
            <p className="text-sm text-destructive">{errors.ends_at.message as string}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="message">Message to users</Label>
        <Textarea id="message" placeholder="We'll be back at 10am UTC" {...register('message')} />
        {errors.message && (
          <p className="text-sm text-destructive">{errors.message.message as string}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id="is_active"
          checked={isActive}
          onCheckedChange={(c) => setValue('is_active', c)}
        />
        <Label htmlFor="is_active">Active</Label>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
